'use client';

import { Analytics } from '@vercel/analytics/react';
import { ReactElement, useEffect, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';

import { UserDisplayNameModal } from '../UserDisplayNameModal';

import AuthWrapper from './AuthWrapper';
import FeatureFlagWrapper from './FeatureFlagWrapper';
import IntercomProviderWrapper from './IntercomProviderWrapper';

import { TermsOfServiceModal } from '~/components/TermsOfService';
import { SettingsModalProvider } from '~/contexts/SettingsModalContext';
import { useHeap, useUserpilot } from '~/hooks';
import useUserInfo from '~/hooks/useUserInfo';
import { AppStore } from '~/redux/store';

enum AppRootModal {
  DISPLAY_NAME = 'DISPLAY_NAME',
  TERMS_OF_SERVICE = 'TERMS_OF_SERVICE'
}

const ProviderWrapper = ({
  children,
  store
}: {
  children: React.ReactElement | ReactElement[];
  store: AppStore;
}) => {
  // Call setup hooks
  useHeap();
  const { triggerUserpilotTour } = useUserpilot();

  // State
  const [openModal, setOpenModal] = useState<AppRootModal | null>(null);

  // Queries
  const { user, refetchUser } = useUserInfo();

  const signedToS = user?.signedTosAt !== null;

  useEffect(
    () => {
      if (!user) {
        setOpenModal(null);
        return;
      }

      if (!signedToS) {
        setOpenModal(AppRootModal.TERMS_OF_SERVICE);
      } else if (!user.name) {
        setOpenModal(AppRootModal.DISPLAY_NAME);
      } else {
        setOpenModal(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signedToS, user]
  );

  const completeOnboarding = () => {
    void refetchUser();
    setOpenModal(null);
    // Trigger Userpilot tour manually to avoid collision with the modal
    triggerUserpilotTour();
  };

  return (
    <ReduxProvider store={store}>
      <IntercomProviderWrapper>
        <AuthWrapper>
          <FeatureFlagWrapper>
            <SettingsModalProvider>
              {children}
              {openModal === AppRootModal.DISPLAY_NAME && (
                <UserDisplayNameModal onComplete={completeOnboarding} />
              )}
              {openModal === AppRootModal.TERMS_OF_SERVICE && (
                <TermsOfServiceModal
                  onComplete={() => {
                    void refetchUser();
                    setOpenModal(null);
                  }}
                />
              )}
            </SettingsModalProvider>
          </FeatureFlagWrapper>
        </AuthWrapper>
        <Analytics />
      </IntercomProviderWrapper>
    </ReduxProvider>
  );
};

export default ProviderWrapper;
